/*
  individual-product.js
  IMR-specific hydration:
    1. Apply color buckets (is-positive / is-neutral / is-negative) to
       each `.individual-product__performance-bar-fill` based on its
       parent bar's data-value. Inline width is already set server-side
       from the metaobject; this only paints the color class.
    2. Populate any `<table class="scrollable-table sleeper-ratings">`
       in the article body with sleeper-preference scores from the
       hydration globals emitted by `snippets/individual-product-mattress-card.liquid`.

  Generic article-body wiring (sticky table shadow, sleep-expert
  blockquote hydration, TOC scroll-spy, sticky CTA) is provided by
  `assets/best-product.js`, which the section also loads. The bootstrap
  in best-product.js initializes any section with
  `data-section-type="individual-product"` so we get all of that
  behavior for free.
*/

(function () {
  'use strict';

  function bucket(value) {
    if (value >= 7) return 'is-positive';
    if (value >= 4) return 'is-neutral';
    return 'is-negative';
  }

  function paintPerformanceBars(root) {
    var bars = root.querySelectorAll('.individual-product__performance-bar');
    bars.forEach(function (bar) {
      var fill = bar.querySelector('.individual-product__performance-bar-fill');
      if (!fill) return;
      var raw = parseFloat(bar.getAttribute('data-value'));
      if (isNaN(raw)) return;
      fill.classList.remove('is-positive', 'is-neutral', 'is-negative');
      fill.classList.add(bucket(raw));
    });
  }

  /* Body-content .sd-performance-ratings blocks
     ────────────────────────────────────────────────────────────────
     The legacy WP body emits a list of `<div class="sd-performance-ratings">`
     panels — one per metric — with placeholder markup that we need to
     hydrate from `window.individualProductMetrics.performance`:
       - Read the H3 text → derive a slug key
       - Tag the <i> icon with the matching `sd-icon-{slug}` class
       - Set `data-p-label` on the rating-content for later debugging
       - Set the graph-bar's data-value, fill width, and color bucket
       - Replace the rating-value text with the real metric value
  */
  function hydrateBodyPerformanceRatings(root) {
    var metrics = window.individualProductMetrics && window.individualProductMetrics.performance;
    if (!metrics) return;
    var blocks = root.querySelectorAll('.sd-performance-ratings');
    if (!blocks.length) return;

    blocks.forEach(function (block) {
      var heading = block.querySelector('.sd-performance-heading');
      var h3 = heading && heading.querySelector('h3');
      if (!h3) return;
      var slug = h3.textContent.trim().toLowerCase().replace(/\s+/g, '-');

      var icon = heading.querySelector('i');
      if (icon) {
        // Drop any stale modifier classes from the legacy markup before
        // applying the one that matches the heading text.
        icon.className = 'sd-icon-' + slug;
      }

      var content = block.querySelector('.sd-performance-rating-content');
      if (!content) return;
      content.setAttribute('data-p-label', slug);

      var value = metrics[slug];
      if (typeof value !== 'number') return;

      var bar = content.querySelector('.graph-bar');
      var fill = content.querySelector('.graph-bar-fill');
      var ratingValue = content.querySelector('.rating-value');
      if (bar) bar.setAttribute('data-value', value);
      if (fill) {
        var pct = Math.min(100, Math.max(0, value * 10));
        fill.style.width = pct + '%';
        fill.classList.remove('positive', 'neutral', 'negative', 'is-positive', 'is-neutral', 'is-negative');
        // Bucket class — matches the legacy theme's plain class names
        // (the CSS in this section is written to target both, but the
        // legacy `positive` / `neutral` / `negative` shorter names keep
        // any pasted-in body markup with hard-coded classes working).
        if (value >= 7) {
          fill.classList.add('positive');
        } else if (value >= 4) {
          fill.classList.add('neutral');
        } else {
          fill.classList.add('negative');
        }
      }
      if (ratingValue) ratingValue.textContent = value.toFixed(1);
    });
  }

  function fillSleeperRatingsTable(root) {
    var metrics = window.individualProductMetrics && window.individualProductMetrics.preference;
    if (!metrics) return;
    var table = root.querySelector('table.scrollable-table.sleeper-ratings, .scrollable-table.sleeper-ratings table');
    if (!table) return;
    var tbody = table.querySelector('tbody');
    if (!tbody) return;
    var rows = tbody.querySelectorAll('tr');

    // Convention from the legacy script: rows 1–3 (after the header row)
    // correspond to Side / Back / Stomach sleepers; columns 1–3 (after
    // the row label) correspond to light / average / heavy weight.
    var rowMap = [
      ['side-sleeper-light', 'side-sleeper-average', 'side-sleeper-heavy'],
      ['back-sleeper-light', 'back-sleeper-average', 'back-sleeper-heavy'],
      ['stomach-sleeper-light', 'stomach-sleeper-average', 'stomach-sleeper-heavy']
    ];

    Array.prototype.slice.call(rows, 1).forEach(function (row, rowIdx) {
      var keys = rowMap[rowIdx];
      if (!keys) return;
      var cells = row.querySelectorAll('td');
      Array.prototype.slice.call(cells, 1).forEach(function (cell, colIdx) {
        var key = keys[colIdx];
        if (!key) return;
        var v = metrics[key];
        if (typeof v !== 'number') return;
        cell.textContent = v.toFixed(1) + '/10';
      });
    });
  }

  /* Editorial-process tooltip
     ────────────────────────────────────────────────────────────────
     The header uses `metaobject-article-header`, which embeds an
     `metaobject-article-editorial-tooltip` snippet (info icon → small
     popover with editorial-process blurb + "Our methodology" link).
     The metaobject-article section ships JS for this, but its
     bootstrap scopes to `[data-section-type="metaobject-article"]`, so
     we add a local handler here. Mirrors the upstream logic verbatim. */
  function initEditorialTooltip(root) {
    var icon = root.querySelector('[data-editorial-icon]');
    var blurb = root.querySelector('[data-editorial-blurb]');
    if (!icon || !blurb) return;

    blurb.removeAttribute('hidden');

    function open() {
      blurb.classList.add('is-open');
      icon.setAttribute('aria-expanded', 'true');
    }
    function close() {
      blurb.classList.remove('is-open');
      icon.setAttribute('aria-expanded', 'false');
    }

    icon.addEventListener('click', function (e) {
      e.stopPropagation();
      blurb.classList.contains('is-open') ? close() : open();
    });

    icon.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        blurb.classList.contains('is-open') ? close() : open();
      } else if (e.key === 'Escape') {
        close();
      }
    });

    document.addEventListener('click', function (e) {
      if (!icon.contains(e.target) && !blurb.contains(e.target)) close();
    });
  }

  function init(root) {
    if (!root) return;
    paintPerformanceBars(root);
    hydrateBodyPerformanceRatings(root);
    fillSleeperRatingsTable(root);
    initEditorialTooltip(root);
  }

  function bootstrap() {
    document.querySelectorAll('[data-section-type="individual-product"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-section-type="individual-product"]');
    if (root) init(root);
  });
})();
